import { DeviceType } from "./ChannelType";

export type GenericProductType = "Button" | "Actor" | "Sensor" | "DecentralActor" | "Other";
export type ButtonType = "Simple" | "Touch" | "Panel";

export type BaseDeviceProps = {
  readonly actorBaseProps?: BaseActorProps;
  readonly buttonBaseProps?: BaseButtonProps;
};

export type BaseActorProps = {
  readonly channels: number;
  readonly deviceType: DeviceType;
};

export type BaseButtonProps = {
  readonly type: ButtonType;
  readonly statusLeds: number;
  readonly hasDisplay: boolean;
  readonly supportsSplitRocker: boolean;
  /** 0: undefined, 1: supports long press, 2: supports extra long press (supportsExtraLongPress). */
  readonly supportsLongPress: 0 | 1 | 2;
  readonly numOfPages: number;
  readonly supportsSlap: 0 | 1 | 2;
  readonly horizontal: boolean;
  readonly color: string;
  readonly heightMm: number;
  readonly widthMm: number;
  readonly numberOfButtonRows: number;
};

/** Creates a detached set of button properties. */
export function createBaseButtonProps(params: Partial<BaseButtonProps> = {}): BaseButtonProps {
  return {
    type: params.type ?? "Simple",
    statusLeds: params.statusLeds ?? 0,
    hasDisplay: params.hasDisplay ?? false,
    supportsSplitRocker: params.supportsSplitRocker ?? true,
    supportsLongPress: params.supportsLongPress ?? 0,
    numOfPages: params.numOfPages ?? 1,
    supportsSlap: params.supportsSlap ?? 0,
    horizontal: params.horizontal ?? false,
    color: params.color ?? "",
    heightMm: params.heightMm ?? 90,
    widthMm: params.widthMm ?? 90,
    numberOfButtonRows: params.numberOfButtonRows ?? 1,
  };
}

export function baseButtonDefaults(subType?: string): BaseButtonProps {
  if (subType === "Touch") return createBaseButtonProps({ type: "Touch", numberOfButtonRows: 3, horizontal: false, hasDisplay: true });
  if (subType === "Panel") return createBaseButtonProps({ type: "Panel" });
  return createBaseButtonProps({ type: "Simple", numberOfButtonRows: 2, horizontal: true });
}

export function baseActorDefaults(subType?: string): BaseActorProps {
  const types: Record<string, DeviceType> = {
    Switching: DeviceType.Switch, Dimming: DeviceType.Dimmer, Shading: DeviceType.Jalousie,
    Heating: DeviceType.Heating, BinaryInputs: DeviceType.Input,
  };
  return { channels: 1, deviceType: types[subType ?? ""] ?? DeviceType.Switch };
}

export function createBaseDeviceProps(params: BaseDeviceProps = {}): BaseDeviceProps {
  return {
    ...(params.buttonBaseProps ? { buttonBaseProps: createBaseButtonProps(params.buttonBaseProps) } : {}),
    ...(params.actorBaseProps ? { actorBaseProps: { ...params.actorBaseProps } } : {}),
  };
}
